import { ReceiptIcon } from '@phosphor-icons/react';
import { Button } from '@sr/ui-library';
import { useLocation, useParams } from 'react-router-dom';
import { BackButton } from '../../../../components/BackButton';
import { EmbeddedWorkflowView } from '../../../../components/EmbeddedWorkflowView';
import { EntityList } from '../../../../components/EntityList';
import { PageHeader } from '../../../../components/PageHeader';
import { INDUSTRY_EMAIL } from '../../../../constant/Common';
import { useEmbeddedWorkflowActive } from '../../../../hooks/useEmbeddedWorkflowActive';
import { useEmbeddedWorkflowBack } from '../../../../hooks/useEmbeddedWorkflowBack';
import { MSR3BaseLayout } from '../../../../layout/MSR3BaseLayout';
import { AddTitleOptionCard } from './AddTitleOptionCard';
import { RegisterTitleAction } from './RegisterTitleAction';

/** The entry point that opened this page, recorded by `AddTitleLink`. */
const getOpenedFrom = (state: unknown): string | undefined => {
  const addTitleFrom = (state as { addTitleFrom?: unknown } | null)
    ?.addTitleFrom;

  return typeof addTitleFrom === 'string' ? addTitleFrom : undefined;
};

/**
 * Interim page between the "Add Title" entry points and the MSR2 registration
 * embed: the user picks what they are registering, and the royalties action
 * resolves to registration or the membership application (APP-2253).
 */
export const AddTitleV3 = () => {
  const { workspaceId } = useParams();
  const { state } = useLocation();

  // "Register Title" appends `?src=embed/add-title/...`; render the embed in
  // place of the cards when it does, mirroring Home and the Titles page.
  const isEmbeddingWorkflow = useEmbeddedWorkflowActive();

  // Carried in location state (which survives a reload) rather than a ref:
  // both the "Register Title" link and closing the workflow pass it through,
  // so it is still here after a round trip via the embed.
  const openedFrom = getOpenedFrom(state);

  const handleBackClick = useEmbeddedWorkflowBack(
    openedFrom ?? (workspaceId ? `/workspace/${workspaceId}/titles` : '/'),
  );

  return (
    <MSR3BaseLayout
      pageHeader={
        <PageHeader
          title="Add Title"
          breadcrumb={<BackButton onClick={handleBackClick} />}
        />
      }
    >
      {isEmbeddingWorkflow ? (
        <EmbeddedWorkflowView />
      ) : (
        <EntityList>
          <EntityList.Content>
            <AddTitleOptionCard
              title="Add Secondary Royalties"
              action={<RegisterTitleAction />}
              description="Register a title to collect Screenrights royalties across ANZ and international markets."
              icon="currencyCircleDollar"
            />

            <AddTitleOptionCard
              title="Collection Account & Residuals"
              action={
                <Button asChild variant="outline">
                  <a href={`mailto:${INDUSTRY_EMAIL}`}>Contact us</a>
                </Button>
              }
              description={
                <>
                  Contact us to set up a title for collection account or
                  residuals.{' '}
                  <a
                    className="text-primary underline"
                    href={`mailto:${INDUSTRY_EMAIL}`}
                  >
                    {INDUSTRY_EMAIL}
                  </a>
                </>
              }
              icon={ReceiptIcon}
            />
          </EntityList.Content>
        </EntityList>
      )}
    </MSR3BaseLayout>
  );
};
